<template>
  <div>
    <div
      v-if="apparatus"
      class="card hoverPointer apparatus-card"
      :class="getApparatusCardClass()"
      @click="toDetails()"
      :header-bg-variant="getHeaderBGVariant()"
      header-class="dashboard-card-header"
      footer-class="apparatus-card-footer"
      data-test-hook="apparatus-clickable-card-apparatus-card"
    >
      <!--Header-->
      <div v-if="!generatorEnrollmentComplete() || (generatorIsDunning() && !apparatus.isDealerManaged)">
        <div class="card-header dashboard-card-header bg-statusWarning">
          <div data-test-hook="DisconnectedHeader" class="row w-100 no-gutters">
            <div class="float-left col">
              <strong v-if="generatorIsDunning()" data-test-hook="HeaderText" class="py-0 my-0">
                {{ $t('xUpdateYourPaymentMethod') }}
              </strong>
              <strong v-else data-test-hook="HeaderText" class="py-0 my-0">
                {{ $t('xFinishSettingUpYourProduct') }}
              </strong>
            </div>
            <div class="float-right mr-0 pr-0 col-auto">
              <i class="fa-solid fa-arrow-right" />
            </div>
          </div>
        </div>
      </div>
      <div v-else-if="generatorDealerSubscriptionExpired()">
        <div class="card-header dashboard-card-header bg-statusWarning">
          <div data-test-hook="NoSubscriptionHeader" class="row w-100 no-gutters">
            <div class="float-left col">
              <strong data-test-hook="HeaderText" class="py-0 my-0">
                {{ $t('xYourDealerManagedSubscriptionHasExpired') }}
              </strong>
            </div>
            <div class="float-right mr-0 pr-0 col-auto">
              <i class="fa-solid fa-arrow-right" />
            </div>
          </div>
        </div>
      </div>
      <div v-else-if="hsbHasDealerAndActivePremiumSub()">
        <div class="card-header dashboard-card-header bg-statusWarning">
          <div data-test-hook="GeneratorInDealerPlanAndPremiumPlan" class="row w-100 no-gutters">
            <div class="float-left col">
              <strong data-test-hook="HeaderText" class="py-0 my-0">
                {{ $t('xYourAccountNeedsAttentionHeader') }}
              </strong>
            </div>
            <div class="float-right mr-0 pr-0 col-auto">
              <i class="fa-solid fa-arrow-right" />
            </div>
          </div>
        </div>
      </div>
      <div v-else-if="basicHSBWithExistingSubscription()">
        <div class="card-header dashboard-card-header bg-statusWarning">
          <div data-test-hook="NotCoveredGenerator" class="row w-100 no-gutters">
            <div class="float-left col">
              <strong data-test-hook="HeaderText" class="py-0 my-0">
                {{ $t('xYourAccountNeedsAttentionHeader') }}
              </strong>
            </div>
            <div class="float-right mr-0 pr-0 col-auto">
              <i class="fa-solid fa-arrow-right" />
            </div>
          </div>
        </div>
      </div>
      <div v-else-if="warningHeader">
        <div class="card-header dashboard-card-header bg-statusWarning">
          <div data-test-hook="WarningHeader" class="row w-100 no-gutters">
            <div class="float-left col">
              <strong data-test-hook="HeaderText" class="py-0 my-0">
                {{ $t('xWarningDetails') }}
              </strong>
            </div>
            <div class="float-right mr-0 pr-0 col-auto">
              <i class="fa-solid fa-arrow-right" />
            </div>
          </div>
        </div>
      </div>
      <div v-else-if="faultHeader">
        <div class="card-header dashboard-card-header bg-statusFault">
          <div data-test-hook="FaultHeader" class="row w-100 no-gutters">
            <div class="float-left col">
              <strong data-test-hook="HeaderText" class="py-0 my-0 colorWhite">
                {{ $t('xFaultDetails') }}
              </strong>
            </div>
            <div class="float-right mr-0 pr-0 col-auto">
              <i class="fa-solid fa-arrow-right" />
            </div>
          </div>
        </div>
      </div>
      <div v-else-if="disconnectedHeader">
        <div class="card-header dashboard-card-header bg-statusDisconnected">
          <div data-test-hook="DisconnectedHeader" class="row w-100 no-gutters">
            <div class="float-left col">
              <strong data-test-hook="HeaderText" class="py-0 my-0">
                {{ noSignalMessage() }}
              </strong>
            </div>
            <div class="float-right mr-0 pr-0 col-auto">
              <i class="fa-solid fa-arrow-right" />
            </div>
          </div>
        </div>
      </div>
      <div v-else-if="switchedOffHeader">
        <div class="card-header dashboard-card-header bg-statusFault">
          <div data-test-hook="switchedOffHeader" class="row w-100 no-gutters">
            <div class="float-left col">
              <strong data-test-hook="HeaderText" class="py-0 my-0 colorWhite">
                {{ $t('xAttentionRequired') }}
              </strong>
            </div>
            <div class="float-right mr-0 pr-0 col-auto">
              <i class="fa-solid fa-arrow-right" style="color: white" />
            </div>
          </div>
        </div>
      </div>
      <div v-else>
        <div class="card-header dashboard-card-header">
          <div data-test-hook="StandardHeader" class="center row w-100 no-gutters">
            <div v-if="apparatus.weather" data-test-hook="ApparatusWeather" class="float-left col-auto">
              <i :class="apparatus.weather.iconCode | weatherIcon"></i>
              {{ temperature }}&deg;{{ temperatureUnits }}
            </div>
            <div
              v-if="device && device[0] && device[0].value && device[0].value.signalStrength"
              class="float-left col-auto ml-2"
            >
              <i v-if="getDeviceType() == wifi" class="fa-solid fa-wifi" data-test-hook="ApparatusSignalStrength" />
              <i v-else class="fal fa-signal-alt" data-test-hook="ApparatusSignalStrength" />
              {{ device[0].value.signalStrength }}
            </div>
            <div class="float-right text-right mr-0 pr-0 col">
              <i class="fa-solid fa-arrow-right" />
            </div>
          </div>
        </div>
      </div>
      <div :class="apparatus.isConnecting ? 'connectingOverlay' : null"></div>
      <div class="card-body">
        <!--Signal-->
        <div v-if="!basicHSBWithExistingSubscription()" class="row d-flex">
          <div class="col-auto">
            <h4 class="mb-0 py-2 pl-1 d-flex align-items-center">
              <StatusIcon
                class="mr-1 statusIcon"
                :apparatusStatus="apparatus.apparatusStatus"
                :isConnected="apparatus.isConnected"
                :showWarning="apparatus.showWarning"
                :isConnecting="apparatus.isConnecting"
                :deviceType="getDeviceType()"
                data-test-hook="ApparatusStatusIcon"
              />
              <span data-test-hook="ApparatusStatusText">{{ getStatusText() }}</span>
            </h4>
          </div>
        </div>
        <div v-else class="row d-flex">
          <div class="col-auto">
            <h4 class="mb-0 py-2 pl-1 d-flex align-items-center">
              <StatusIcon
                class="mr-1 statusIcon"
                :apparatusStatus="apparatus.apparatusStatus"
                :isConnected="false"
                :showWarning="true"
                :isConnecting="false"
                :deviceType="getDeviceType()"
                data-test-hook="ApparatusStatusIcon"
              />
              <span data-test-hook="ApparatusStatusText">{{ $t('xNoSignal') }}</span>
            </h4>
          </div>
        </div>
        <!--Hero Image and Address-->
        <div class="row d-flex">
          <div class="col-4">
            <img :src="apparatus.heroImageUrl" data-test-hook="ApparatusHeroImg" alt="Hero Image" class="img-fluid" />
          </div>
          <div class="pt-4 col-8">
            <div class="row">
              <div data-test-hook="ApparatusAddress" class="ellipsis-overflow mb-2 col">
                {{ apparatus.localizedAddress }}
              </div>
            </div>
            <div class="row">
              <div class="col">
                <h2 data-test-hook="ApparatusName" class="pb-1 mb-0">
                  {{ apparatus.name }}
                </h2>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!--Footer-->
      <div class="card-footer apparatus-card-footer">
        <div v-if="generatorEnrollmentComplete()" class="row">
          <div class="col">
            <span v-if="apparatus.isDealerManaged || apparatus.isDealerUnmonitored" class="text-small">{{
              'xDealerManaged' | formatLabel
            }}</span>
            <span v-else class="text-small">{{ 'xPreferredDealer' | formatLabel }}</span>
          </div>
        </div>
        <div v-if="generatorEnrollmentComplete()" class="row">
          <div v-if="apparatus && apparatus.preferredDealerName" class="col">
            <p data-test-hook="ApparatusPreferredName" class="font-weight-bold">
              <i class="fal fa-user-hard-hat"></i>
              {{ apparatus.preferredDealerName }}
            </p>
          </div>
          <div v-else class="col">
            <p data-test-hook="ApparatusPreferredName" class="font-weight-bold">
              <i class="fal fa-user-hard-hat"></i>
              {{ $t('xNoneSelected') }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <!--Basic gen with existing active plan-->
    <div
      v-if="apparatus && apparatus.apparatusId"
      :id="'basic-gen-with-existing-subscription' + apparatus.apparatusId"
      role="dialog"
      class="modal fade show"
    >
      <div class="modal-dialog modal-dialog-centered">
        <span tabindex="0"></span>
        <div tabindex="-1" class="modal-content">
          <header class="modal-header">
            <div class="row w-100 no-gutters align-items-center">
              <div class="col">
                <h2 class="modal-title">
                  <i class="far fa-exclamation-triangle colorOrange mr-1" />
                  {{ $t('xYourAccountNeedsAttentionHeaderCapitalized') }}
                </h2>
              </div>
              <div class="float-right col-auto">
                <button
                  type="button"
                  class="btn btn-icon-button--gray btn-icon-button"
                  @click="closeBasicGenWithExistingSubscriptionModal()"
                >
                  <i class="fas fa-times" />
                </button>
              </div>
            </div>
          </header>
          <div class="modal-body">
            <div class="row">
              <div
                v-if="doesRequireUpgrade(organizationSubscriptionDetails?.subscriptions, apparatusesToUpgrade)"
                class="col"
              >
                <p>
                  {{ $t('xThisGeneratorNotIncludedInAPlan') }}
                  <a href="https://www.generac.com/dealer-locator/" target="_blank">
                    {{ $t('xThisProductNotIncludedFindDealer') }}
                  </a>
                </p>
              </div>
              <div v-else class="col">
                <p>
                  {{ $t('xThisGeneratorNotIncludedInAPlanUpdate') }}
                </p>
              </div>
            </div>
          </div>
          <ModalFooter>
            <button
              type="button"
              class="btn btn-secondary"
              @click="
                closeBasicGenWithExistingSubscriptionModal(), $refs.removeApparatusModal.checkApparatusSubscription()
              "
              :disabled="billingMaintenanceFeatureFlagEnabled(currentAppConfig)"
            >
              {{ $t('xRemoveFromAccount') }}
            </button>
            <button
              data-test-hook="apparatus-card-account-needs-attention-upgrade-now-button"
              type="button"
              @click="checkSubscriptionBeforeOpeningUpgradeOrUpdateSubscriptionModal()"
              class="btn btn-primary"
              :disabled="billingMaintenanceFeatureFlagEnabled(currentAppConfig)"
            >
              <div v-if="doesRequireUpgrade(organizationSubscriptionDetails?.subscriptions, apparatusesToUpgrade)">
                {{ $t('xUpgradeNow') }}
              </div>
              <div v-else>{{ $t('xUpdateNow') }}</div>
            </button>
          </ModalFooter>
        </div>
      </div>
    </div>

    <!-- Manage Credit Card Modal -->
    <ManageGracePeriodModals
      v-if="generatorIsDunning()"
      :isWebSubscription="subscriptionStoreIsWebOrLegacy"
      :organizationSubscriptionDetails="organizationSubscriptionDetails"
      :apparatusId="apparatusInfo.apparatusId"
      :apparatusType="apparatusInfo.type"
      :accountProfile="accountProfile"
      :subscriptionStatus="
        this.subscription && this.subscription[0] && this.subscription[0].value && this.subscription[0].value.status
          ? this.subscription[0].value.status
          : null
      "
      :paymentInfo="paymentInfo"
    />

    <MultipleChargesModal
      v-if="apparatus && hsbSubscriptionDetails"
      :apparatus="apparatus"
      :currentPremiumSubscription="hsbSubscriptionDetails"
    />

    <MobileSubscriptionModal
      v-if="apparatus"
      :modalId="`mobile-sub-modal-${apparatus.apparatusId}`"
      :store="mobileSubscriptionStore"
      :apparatusType="apparatus.type"
      :requireUpgrade="doesRequireUpgrade(organizationSubscriptionDetails?.subscriptions, apparatusesToUpgrade)"
      @removePressed="$refs.removeApparatusModal.checkApparatusSubscription()"
    />

    <ProductsOverLimitModal
      v-if="apparatus"
      :modalId="`products-over-limit-modal-${apparatus.apparatusId}`"
      :apparatusType="apparatus.type"
      @goToRemoveFlow="$refs.removeApparatusModal.checkApparatusSubscription()"
    />

    <UpgradeOrUpdateSubscriptionsModal
      v-if="apparatus && apparatus.apparatusId && basicHSBWithExistingSubscription()"
      :modalId="`upgrade-subscription-modal-${apparatus.apparatusId}`"
      :apparatus-id="apparatus.apparatusId"
      :apparatus-list="apparatusesToUpgrade"
      :accountProfile="accountProfile"
    />

    <PendingPlanChangeModal
      v-if="apparatus"
      :modalId="`pending-plan-change-modal-${apparatus.apparatusId}`"
      @onYesContinueClick="openUpgradeOrUpdateSubscriptionModal"
    />
    <PendingPlanCancelModal
      v-if="apparatus"
      :modalId="`pending-plan-cancel-modal-${apparatus.apparatusId}`"
      :planCanceledDate="pendingCancelRenewalDate"
    />

    <RemoveApparatus v-if="apparatus" ref="removeApparatusModal" :apparatus="apparatus" :showButton="false" />
  </div>
</template>

<script>
import { mapState } from 'vuex'
import StatusIcon from '@/components/StatusIcon'
import RemoveApparatus from '@/components/ApparatusDetails/RemoveApparatus'
import MultipleChargesModal from './MultipleChargesModal.vue'
import * as Constants from '@/constants'
import {
  getSubscriptionForApparatus,
  getLegacySubscriptionForApparatus,
  subscriptionDetailActive,
  doesRequireUpgrade
} from '@/utils/mixins'
import MobileSubscriptionModal from '../Enrollment/Subscription/MobileSubscriptionModal.vue'
import UpgradeOrUpdateSubscriptionsModal from '../Common/Subscriptions/UpgradeOrUpdateSubscriptionsModal.vue'
import ManageGracePeriodModals from './ManageGracePeriodModals.vue'
import PendingPlanChangeModal from '../Common/Subscriptions/PendingPlanChangeModal.vue'
import PendingPlanCancelModal from '../Common/Subscriptions/PendingPlanCancelModal.vue'
import ProductsOverLimitModal from '../Common/Subscriptions/ProductsOverLimitModal.vue'

export default {
  components: {
    StatusIcon,
    ManageGracePeriodModals,
    MultipleChargesModal,
    MobileSubscriptionModal,
    UpgradeOrUpdateSubscriptionsModal,
    PendingPlanChangeModal,
    PendingPlanCancelModal,
    RemoveApparatus,
    ProductsOverLimitModal
  },
  props: ['apparatuses', 'apparatusInfo', 'paymentInfo', 'organizationSubscriptionDetailsData', 'apparatusesToUpgrade'],
  data() {
    return {
      device: null,
      subscription: null,
      startSubFlow: false,
      apparatus: null,
      FreeSub: Constants.FreeSubscription,
      NoSub: Constants.NoSubscription,
      temperature: 0,
      temperatureUnits: 'F',
      wifi: Constants.HsbWifiDeviceType,
      organizationSubscriptionDetails: null,
      hsbSubscriptionDetails: null,
      hsbLegacySubscriptionDetails: null,
      mobileSubscriptionStore: null,
      subscriptionStoreIsWebOrLegacy: false,
      pendingCancelRenewalDate: null
    }
  },
  computed: {
    ...mapState({
      accountProfile: state => state.account.accountProfile,
      user: state => state.account.user,
      currentAppConfig: state => state.application.currentAppConfig
    }),
    billingMigrationFeatureFlag() {
      return Constants.BillingMigrationFeatureFlag
    },
    warningHeader() {
      if (this.apparatus.isConnecting) return false
      if (!this.apparatus.isConnected) return false
      return this.apparatus.apparatusStatus == Constants.StatusIsWarning
    },
    faultHeader() {
      if (this.apparatus.isConnecting) return false
      if (!this.apparatus.isConnected) return false
      return this.apparatus.apparatusStatus == Constants.StatusIsFault
    },
    switchedOffHeader() {
      if (this.apparatus.isConnecting) return false
      if (!this.apparatus.isConnected) return false
      return this.apparatus.apparatusStatus == Constants.StatusIsSwitchedOff
    },
    disconnectedHeader() {
      return (
        !this.apparatus.isConnecting &&
        (!this.apparatus.isConnected || this.apparatus.apparatusStatus == Constants.StatusIsCommsIssue)
      )
    },
    apparatusesOverLimit() {
      let hsbCount = 0
      let fmCount = 0
      this.apparatuses?.forEach(apparatus => {
        if (!apparatus.isDealerManaged) {
          if (apparatus.type === Constants.HsbApparatusType) hsbCount++
          else if (apparatus.type === Constants.FuelTankApparatusType) fmCount++
        }
      })

      return hsbCount > Constants.HSB_MAX_AMOUNT || fmCount > Constants.FUEL_MONITOR_MAX_AMOUNT
    }
  },
  methods: {
    doesRequireUpgrade,
    getStatusText() {
      if (!this.apparatus.isConnected) return 'No Signal'
      else if (this.apparatus.apparatusStatus == Constants.StatusIsWarning) return 'Warning'
      else if (this.apparatus.apparatusStatus == Constants.StatusIsFault) return 'Stopped'
      else if (this.apparatus.apparatusStatus == Constants.StatusIsRunning) return 'Running'
      else if (this.apparatus.apparatusStatus == Constants.StatusIsReady) return 'Ready To Run'
      else if (this.apparatus.apparatusStatus == Constants.StatusIsExercising) return 'Exercising'
      else if (this.apparatus.apparatusStatus == Constants.StatusIsCommsIssue) return 'Trouble Communicating'
      else if (this.apparatus.apparatusStatus == Constants.StatusIsSwitchedOff) return 'Switched Off'
      else return 'Status Unknown'
    },
    getHeaderBGVariant() {
      if (!this.generatorEnrollmentComplete()) return 'statusWarning'
      else if (this.disconnectedHeader) return 'statusDisconnected'
      else if (this.warningHeader) return 'statusWarning'
      else if (this.faultHeader) return 'statusFault'
      else if (this.switchedOffHeader) return 'statusSwitchedOff'
    },
    getApparatusCardClass() {
      if (this.apparatus && this.generatorEnrollmentComplete() && !this.basicHSBWithExistingSubscription()) {
        if (this.apparatusInfo.isConnecting) return ''
        else return this.apparatus.isConnected ? `apparatusCard${this.apparatus.apparatusStatus}` : ''
      }
    },
    openEnrollmentFlow() {
      if (
        this.currentAppConfig &&
        this.currentAppConfig.featureFlags &&
        !this.currentAppConfig.featureFlags[this.billingMigrationFeatureFlag]
      ) {
        this.$emit('incompleteApparatus', {
          id: this.apparatus.apparatusId,
          device: this.device,
          subscription: this.subscription,
          modelNumber: this.apparatus.modelNumber
        })
      }
    },
    generatorEnrollmentComplete() {
      if (!this.apparatus) return false
      if (this.apparatus.type != Constants.HsbApparatusType) {
        return true
      }
      if (!this.apparatus.properties) return false
      if (this.device && this.device.length >= 1) {
        return true
      }
      return false
    },
    isBasicHSB() {
      // if it's dealer managed, it's not basic
      if (this.apparatus.isDealerManaged) {
        return false
      }

      // if there's no premium OR legacy subscription at all, it's definitely basic
      if (!this.hsbSubscriptionDetails && !this.hsbLegacySubscriptionDetails) {
        return true
      }

      // if subscription exists, check if cancelled. If there's no subscription, treat it as cancelled
      const isPremiumSubscriptionCancelled = !!this.hsbSubscriptionDetails
        ? this.hsbSubscriptionDetails.status === Constants.SubscriptionDetailCanceled
        : true
      const isLegacySubscriptionCancelled = !!this.hsbLegacySubscriptionDetails
        ? this.hsbLegacySubscriptionDetails.status === Constants.SubscriptionCancelled
        : true

      // if all existing subscriptions are cancelled, it's basic
      if (isPremiumSubscriptionCancelled && isLegacySubscriptionCancelled) {
        return true
      }

      return false
    },
    doesAccountHaveAnExistingSubscription() {
      if (
        //if the account has a premium subscription that isn't canceled, then there is an existing subscription
        this.organizationSubscriptionDetails?.subscriptions?.some(
          subscription =>
            subscription.status !== Constants.SubscriptionDetailCanceled &&
            subscription.store !== Object.values(Constants.SubscriptionStoreTypes).indexOf('Dealer')
        ) ||
        //if the account has a legacy subscription that isn't canceled, then there is an existing subscription
        this.organizationSubscriptionDetails?.legacyBillingProducts?.products?.some(
          product => product.status !== Constants.SubscriptionCancelled
        )
      )
        return true
      else return false
    },
    basicHSBWithExistingSubscription() {
      if (!this.apparatus) return false
      if (this.apparatus.type != Constants.HsbApparatusType) {
        return true
      }

      return this.isBasicHSB() && this.doesAccountHaveAnExistingSubscription()
    },
    goToServiceOps() {
      window.open(Constants.RequestDealerUrl)
    },
    openBasicGenWithExistingSubscriptionModal() {
      document.getElementById('basic-gen-with-existing-subscription' + this.apparatus.apparatusId).style.display =
        'block'
    },
    closeBasicGenWithExistingSubscriptionModal() {
      document.getElementById('basic-gen-with-existing-subscription' + this.apparatus.apparatusId).style.display =
        'none'
    },
    hsbHasDealerAndActivePremiumSub() {
      if (!this.apparatus) return false
      if (this.apparatus.type != Constants.HsbApparatusType) {
        return true
      }

      if (
        this.apparatus.isDealerManaged &&
        this.organizationSubscriptionDetails?.subscriptions?.filter(
          subscription =>
            subscription.apparatuses.some(apparatus => apparatus.apparatusId === this.apparatus.apparatusId) &&
            subscription.status === Constants.SubscriptionDetailActive
        ).length > 1
      ) {
        return true
      }
      return false
    },
    openApparatusWithMultipleSubscriptionsModal() {
      document.getElementById('apparatus-with-multiple-subscriptions' + this.apparatus.apparatusId).style.display =
        'block'
    },
    openHomePremiumApparatusWithMultipleSubscriptionsModal() {
      document.getElementById(
        'home-premium-apparatus-with-multiple-subscriptions' + this.apparatus.apparatusId
      ).style.display = 'block'
    },
    openApparatusWithMultipleSubscriptionsMobilePlatformModal() {
      document.getElementById(
        'apparatus-with-multiple-subscriptions-mobile-platform' + this.apparatus.apparatusId
      ).style.display = 'block'
    },
    openRemoveApparatusWithMultipleSubscriptionsMobilePlatformModal() {
      document.getElementById(
        'remove-apparatus-with-multiple-subscriptions-mobile-platform' + this.apparatus.apparatusId
      ).style.display = 'block'
    },
    openUpgradeOrUpdateSubscriptionModal() {
      this.$bvModal.show(`upgrade-subscription-modal-${this.apparatus.apparatusId}`)
    },
    checkSubscriptionBeforeOpeningUpgradeOrUpdateSubscriptionModal() {
      this.closeBasicGenWithExistingSubscriptionModal()
      if (this.hasPendingProductChange()) {
        this.openPlanPendingChangeModal()
      } else if (this.hasPendingCancelPlan()) {
        this.openPlanPendingCancelModal()
      } else {
        this.openUpgradeOrUpdateSubscriptionModal()
      }
    },
    generatorDealerSubscriptionExpired() {
      if (!this.apparatus) return false
      if (this.apparatus.type != Constants.HsbApparatusType) {
        return true
      }

      const hsbDealerSub = this.organizationSubscriptionDetails?.subscriptions?.find(
        subscription =>
          subscription.store === Object.values(Constants.SubscriptionStoreTypes).indexOf('Dealer') &&
          subscription.apparatuses.find(apparatus => apparatus.apparatusId === this.apparatus.apparatusId)
      )
      // If an HSB sub is cancelled and is a dealer based subscription.
      if (
        this.apparatusInfo?.isDealerManaged &&
        (!hsbDealerSub || hsbDealerSub?.status === Constants.SubscriptionDetailCanceled)
      ) {
        return true
      }

      // If there are no non-legacy subscriptions, turn to legacy billing info if available.
      if (!this.hsbSubscriptionDetails && this.hsbLegacySubscriptionDetails) {
        if (
          this.apparatusInfo &&
          this.apparatusInfo?.isDealerManaged &&
          this.hsbLegacySubscriptionDetails?.status === Constants.SubscriptionCancelled
        ) {
          return true
        }
      }

      return false
    },
    generatorIsDunning() {
      if (!this.apparatus) return false
      if (this.apparatus.type != Constants.HsbApparatusType) {
        return true
      }

      // If the subscription has reasoning GracePeriodStarted then it's considered dunning
      if (
        this.hsbSubscriptionDetails &&
        this.hsbSubscriptionDetails.reason === Constants.SubscriptionDetailsReasonGracePeriodStarted
      ) {
        return true
      }

      // If there are no non-legacy subscriptions, turn to legacy billing info if available.
      if (!this.hsbSubscriptionDetails && this.hsbLegacySubscriptionDetails) {
        if (
          this.hsbLegacySubscriptionDetails.status === Constants.SubscriptionDunning ||
          this.hsbLegacySubscriptionDetails?.dunningState === Constants.DunningInProgress
        ) {
          return true
        }
      }

      return false
    },
    /** Unused. */
    generatorHasDevice() {
      if (!this.apparatus) return false
      if (this.apparatus.type != Constants.HsbApparatusType) {
        return true
      }
      if (!this.apparatus.properties) return false
      if (this.device && this.device.length >= 1) return true
      return false
    },
    /** Unused. */
    generatorHasSubscription() {
      if (!this.apparatus) return false
      if (this.apparatus.type != Constants.HsbApparatusType) return false
      if (!this.apparatus.properties) return false
      if (this.subscription && this.subscription.length >= 1) {
        if (
          this.subscription[0].value.status &&
          (this.subscription[0].value.status == Constants.SubscriptionActive ||
            this.subscription[0].value.status == Constants.SubscriptionPendingCancellation ||
            this.subscription[0].value.status == Constants.SubscriptionPendingTermination ||
            this.subscription[0].value.status == Constants.SubscriptionPendingSuspension ||
            this.subscription[0].value.status == Constants.SubscriptionDunning)
        ) {
          return true
        }
      }
      return false
    },
    toDetails() {
      if (this.generatorEnrollmentComplete() && !this.generatorIsDunning()) {
        this.$appInsights.trackEvent({
          name: 'click-apparatus-card',
          properties: { apparatusId: this.apparatus.apparatusId }
        })
        if (this.generatorDealerSubscriptionExpired()) {
          this.$appInsights.trackEvent({
            name: 'expired-dealer-managed-sub-hsb',
            properties: {
              page: 'dashboard',
              apparatusId: this.apparatus.apparatusId
            }
          })
        } else if (this.hsbHasDealerAndActivePremiumSub()) {
          this.hsbSubscriptionDetails = this.organizationSubscriptionDetails?.subscriptions?.find(
            subscription => subscription.store !== Constants.DealerSubscriptionStore
          )
          switch (this.hsbSubscriptionDetails?.store) {
            case Constants.AppleSubscriptionStore:
              if (
                this.isSingleApparatusSubscription(this.hsbSubscriptionDetails?.product?.productId) ||
                this.hsbSubscriptionDetails.apparatuses.length === 1
              ) {
                this.openApparatusWithMultipleSubscriptionsMobilePlatformModal()
              } else if (this.isHomePremiumSubscription(this.hsbSubscriptionDetails?.product?.productId)) {
                this.openRemoveApparatusWithMultipleSubscriptionsMobilePlatformModal()
              }
              break
            case Constants.GoogleSubscriptionStore:
              if (
                this.isSingleApparatusSubscription(this.hsbSubscriptionDetails?.product?.productId) ||
                this.hsbSubscriptionDetails.apparatuses.length === 1
              ) {
                this.openApparatusWithMultipleSubscriptionsMobilePlatformModal()
              } else if (this.isHomePremiumSubscription(this.hsbSubscriptionDetails?.product?.productId)) {
                this.openRemoveApparatusWithMultipleSubscriptionsMobilePlatformModal()
              }
              break
            case Constants.WebSubscriptionStore:
              if (
                this.isSingleApparatusSubscription(this.hsbSubscriptionDetails?.product?.productId) ||
                this.hsbSubscriptionDetails.apparatuses.length === 1
              ) {
                this.openApparatusWithMultipleSubscriptionsModal()
              } else if (this.isHomePremiumSubscription(this.hsbSubscriptionDetails?.product?.productId)) {
                this.openHomePremiumApparatusWithMultipleSubscriptionsModal()
              }
              break
            default:
              break
          }
          return
        } else if (this.basicHSBWithExistingSubscription()) {
          this.handleClickForBasicHSBWithExistingSubscription()
          return
        }
        this.$router.push({
          path: `/details/${this.apparatus.apparatusId}/deviceDetails`
        })
      } else if (this.generatorIsDunning()) {
        if (
          this.currentAppConfig &&
          this.currentAppConfig.featureFlags &&
          !this.currentAppConfig.featureFlags[this.billingMigrationFeatureFlag]
        ) {
          // Account has Mobile Subscription
          this.$bvModal.show('ManageCreditCardModal' + this.apparatus.apparatusId)
        }
      } else this.openEnrollmentFlow()
    },
    handleClickForBasicHSBWithExistingSubscription() {
      if (this.accountHasMobileSubscription()) {
        this.$bvModal.show(`mobile-sub-modal-${this.apparatus.apparatusId}`)
        return
      } else if (this.apparatusesOverLimit) {
        this.$bvModal.show(`products-over-limit-modal-${this.apparatus.apparatusId}`)
      } else {
        // Is a web subscription.
        this.openBasicGenWithExistingSubscriptionModal()
        return
      }
    },
    closeModal() {
      document.getElementById('dealer-managed-sub-expired' + this.apparatus.apparatusId).style.display = 'none'
    },
    setTemperatureUnits() {
      if (!this.user.mobileLinkSettings?.displaySettings?.temperatureUom) {
        this.temperatureUnits = 'F'
      } else {
        this.temperatureUnits = this.user.mobileLinkSettings.displaySettings.temperatureUom == 'fahrenheit' ? 'F' : 'C'
      }
    },
    setTemperature() {
      if (
        this.user?.mobileLinkSettings?.displaySettings?.temperatureUom == undefined ||
        this.apparatus?.weather?.temperature.unit == undefined ||
        this.apparatus?.weather?.temperature.value == undefined
      ) {
        return
      }
      if (
        (this.user.mobileLinkSettings.displaySettings.temperatureUom == 'fahrenheit' &&
          this.apparatus.weather.temperature.unit == 'F') ||
        (this.user.mobileLinkSettings.displaySettings.temperatureUom == 'celsius' &&
          this.apparatus.weather.temperature.unit == 'C')
      ) {
        this.temperature = this.apparatus.weather.temperature.value
      } else if (this.user.mobileLinkSettings.displaySettings.temperatureUom == 'fahrenheit') {
        //Celsius to Fahrenheit
        this.temperature = Math.round(this.apparatus.weather.temperature.value * 9) / 5 + 32
      } else if (this.apparatus.weather) {
        //Fahrenheit to Celsius
        this.temperature = Math.round(((this.apparatus.weather.temperature.value - 32) * 5) / 9)
      }
    },
    getDeviceType() {
      if (this.device && this.device[0] && this.device[0].value) {
        let currentDevice = this.device[0].value
        if (currentDevice.deviceType == Constants.HsbG3DeviceType) {
          return currentDevice.networkType
        }
        return currentDevice.deviceType
      }
    },
    noSignalMessage() {
      switch (this.getDeviceType()) {
        case 'wifi':
          return this.$t('xNoWifiSignal')
        case 'eth':
          return this.$t('xNoSignal')
        case 'lte':
        case 'cdma':
          return this.$t('xNoCellularSignal')
        default:
          return this.$t('xDisconnected')
      }
    },
    setSubscriptionDetails() {
      // Prefer the use of hsbSubscriptionDetails over hsbLegacySubscriptionDetails if hsbSubscriptionDetails is defined.
      this.hsbSubscriptionDetails = getSubscriptionForApparatus(
        this.apparatus.apparatusId,
        this.organizationSubscriptionDetails?.subscriptions
      )
      this.hsbLegacySubscriptionDetails = getLegacySubscriptionForApparatus(
        this.apparatus.apparatusId,
        this.organizationSubscriptionDetails?.legacyBillingProducts
      )
    },
    /** Returns whether or not the account has a mobile subscription that is active/pending cancel/pending change. This subscription may or may not include this HSB. */
    accountHasMobileSubscription() {
      const activeSubs = this.organizationSubscriptionDetails?.subscriptions?.filter(sub =>
        subscriptionDetailActive(sub.status)
      )

      if (!activeSubs?.length) {
        return false
      }

      return activeSubs.some(activeSub => {
        if (
          activeSub.store === Constants.AppleSubscriptionStore ||
          activeSub.store === Constants.GoogleSubscriptionStore
        ) {
          this.mobileSubscriptionStore = activeSub.store
          return true
        }

        return false
      })
    },
    hasPendingProductChange() {
      return this.organizationSubscriptionDetails?.subscriptions?.some(
        subscription => subscription.status === Constants.SubscriptionDetailPendingProductChange
      )
    },
    hasPendingCancelPlan() {
      const iapPendingCancelSubscription = this.organizationSubscriptionDetails?.subscriptions?.find(
        subscription => subscription.status === Constants.SubscriptionDetailPendingCancel
      )
      const legacyPendingCancelSubscription = this.organizationSubscriptionDetails?.legacyBillingProducts?.products?.find(
        product => product.status === Constants.SubscriptionPendingCancellation
      )
      if (iapPendingCancelSubscription) this.pendingCancelRenewalDate = iapPendingCancelSubscription.renewalDate
      else if (legacyPendingCancelSubscription)
        this.pendingCancelRenewalDate = legacyPendingCancelSubscription.nextBillingDate
      return iapPendingCancelSubscription || legacyPendingCancelSubscription
    },
    openPlanPendingChangeModal() {
      this.openModal(`pending-plan-change-modal-${this.apparatus.apparatusId}`)
    },
    openPlanPendingCancelModal() {
      this.openModal(`pending-plan-cancel-modal-${this.apparatus.apparatusId}`)
    }
  },
  watch: {
    apparatusInfo: function(newVal, oldVal) {
      if (newVal) {
        this.apparatus = newVal
        this.device = this.apparatus.properties?.filter(x => x.type == Constants.ApparatusPropertyDevice)
        this.subscription = this.apparatus.properties?.filter(x => x.type == Constants.ApparatusPropertySubscription)
      }
    },
    user: function(newVal, oldVal) {
      if (newVal) {
        this.setTemperature()
        this.setTemperatureUnits()
      }
    },
    organizationSubscriptionDetailsData: function(newVal, oldVal) {
      if (newVal) {
        this.organizationSubscriptionDetails = newVal
        this.setSubscriptionDetails()
      }
    }
  },
  mounted() {
    if (this.apparatusInfo) {
      this.apparatus = this.apparatusInfo
      this.device = this.apparatus.properties?.filter(x => x.type == Constants.ApparatusPropertyDevice)
      this.subscription = this.apparatus.properties?.filter(x => x.type == Constants.ApparatusPropertySubscription)
      this.setTemperature()
      this.setTemperatureUnits()
    }
    if (!this.currentAppConfig) this.$store.dispatch('getApplicationConfiguration', 'Any')

    if (this.organizationSubscriptionDetailsData) {
      this.organizationSubscriptionDetails = this.organizationSubscriptionDetailsData
      this.setSubscriptionDetails()
    }

    if (this.organizationSubscriptionDetails?.subscriptions?.length > 0) {
      // Filter by active, non Dealer Managed subscriptions
      const activeSubs = this.organizationSubscriptionDetails?.subscriptions?.filter(
        subs => subs.status !== Constants.SubscriptionDetailCanceled && subs.store !== Constants.DealerSubscriptionStore
      )
      // Check if there is an active sub that was bought through WEB
      if (activeSubs && activeSubs.some(sub => sub.store === Constants.WebSubscriptionStore)) {
        // The sub is from WEB
        this.subscriptionStoreIsWebOrLegacy = true
      } else {
        // The sub is from APPLE or GOOGLE
        this.subscriptionStoreIsWebOrLegacy = false
      }
    }
    if (this.organizationSubscriptionDetails?.legacyBillingProducts?.products?.length > 0) {
      // Filter by active, non Dealer Managed subscriptions
      const activeSubs = this.organizationSubscriptionDetails?.legacyBillingProducts?.products?.filter(
        subs => subs.status !== Constants.SubscriptionCancelled
      )
      // Check if there is an active sub that was bought through WEB
      if (activeSubs?.length > 0) {
        // The sub is from WEB
        this.subscriptionStoreIsWebOrLegacy = true
      }
    }
  }
}
</script>

<style scoped>
.statusIcon {
  font-size: 1.25em;
}
</style>
